<script setup>
// "Importar HTML": colar o código ou enviar .html/.zip. O servidor
// sanitiza, sobe as imagens do zip e cria o modelo no modo HTML.
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import API from 'dashboard/api/marketingEmailTemplates';

const emit = defineEmits(['created']);
const { t } = useI18n();
const dialog = ref(null);
const tab = ref('paste');
const html = ref('');
const file = ref(null);
const name = ref('');
const busy = ref(false);
const fileKey = ref(0);
const MAX_HTML = 2 * 1024 * 1024;
const MAX_ZIP = 20 * 1024 * 1024;

const open = () => {
  tab.value = 'paste';
  fileKey.value += 1;
  html.value = '';
  file.value = null;
  name.value = '';
  dialog.value?.open();
};
defineExpose({ open });

const tooBig = () => {
  if (tab.value === 'file' && file.value) {
    const isZip = /\.zip$/i.test(file.value.name);
    return file.value.size > (isZip ? MAX_ZIP : MAX_HTML);
  }
  return new Blob([html.value]).size > MAX_HTML;
};

const submit = async () => {
  if (busy.value) return;
  if (tooBig()) {
    useAlert(t('EMAIL_TEMPLATES.EDITOR.IMPORT.TOO_BIG'));
    return;
  }
  const form = new FormData();
  if (tab.value === 'file' && file.value) form.append('file', file.value);
  else form.append('html', html.value);
  if (name.value.trim()) form.append('name', name.value.trim());
  busy.value = true;
  try {
    const { data } = await API.importHtml(form);
    dialog.value?.close();
    emit('created', data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('EMAIL_TEMPLATES.EDITOR.IMPORT.ERROR')
    );
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialog"
    type="edit"
    :title="t('EMAIL_TEMPLATES.EDITOR.IMPORT.TITLE')"
    :description="t('EMAIL_TEMPLATES.EDITOR.IMPORT.DESCRIPTION')"
    :confirm-button-label="t('EMAIL_TEMPLATES.EDITOR.IMPORT.SUBMIT')"
    :is-loading="busy"
    :disable-confirm-button="tab === 'file' ? !file : !html.trim()"
    width="2xl"
    @confirm="submit"
  >
    <div class="flex gap-1 self-start rounded-lg bg-n-solid-2 p-1">
      <button
        v-for="key in ['paste', 'file']"
        :key="key"
        type="button"
        class="rounded-md px-3 py-1 text-xs font-medium"
        :class="
          tab === key
            ? 'bg-n-solid-1 text-n-slate-12 shadow'
            : 'text-n-slate-11'
        "
        @click="tab = key"
      >
        {{
          t(
            `EMAIL_TEMPLATES.EDITOR.IMPORT.${key === 'paste' ? 'PASTE' : 'FILE'}`
          )
        }}
      </button>
    </div>
    <textarea
      v-if="tab === 'paste'"
      v-model="html"
      spellcheck="false"
      class="h-64 w-full rounded-lg border border-n-weak bg-n-solid-1 p-2 font-mono text-xs"
    />
    <input
      v-else
      :key="fileKey"
      type="file"
      accept=".html,.htm,.zip"
      class="text-sm"
      @change="file = $event.target.files?.[0] || null"
    />
    <input
      v-model="name"
      data-test="name"
      :placeholder="t('EMAIL_TEMPLATES.EDITOR.IMPORT.NAME')"
      class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
    />
  </Dialog>
</template>
